import { useMemo } from 'react';
import { cn } from 'lib/cn';
import { humanizeEnum } from 'lib/formatters';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'in', label: 'In stock' },
  { value: 'low', label: 'Low stock' },
  { value: 'out', label: 'Out of stock' },
  { value: 'review', label: 'Under review' },
];

/** Stock bucket for an item: 'in' | 'low' | 'out'. */
function stockBucket(item) {
  const quantity = item.stockQuantity;
  if (quantity == null) return 'in';
  if (Number(quantity) <= 0) return 'out';
  if (item.lowStockThreshold != null && Number(quantity) <= Number(item.lowStockThreshold)) {
    return 'low';
  }
  return 'in';
}

/** Whether an item passes the chosen category + status filters. */
export function catalogItemMatches(item, { category, status }) {
  if (category && category !== 'all' && item.category !== category) return false;
  if (!status || status === 'all') return true;
  if (status === 'review') return Boolean(item.underReview);
  return stockBucket(item) === status;
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition',
        active
          ? 'bg-brand-600 text-white'
          : 'bg-white text-slate-600 ring-1 ring-inset ring-surface-300 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}

/** Top filters for the catalog: stock status + category chips (with counts). */
export function CatalogFilters({ items, category, onCategoryChange, status, onStatusChange }) {
  const categories = useMemo(() => {
    const counts = {};
    items.forEach((item) => {
      if (item.category) counts[item.category] = (counts[item.category] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([value, count]) => ({ value, count }));
  }, [items]);

  return (
    <div className="mb-4 space-y-2">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {STATUS_FILTERS.map((filter) => (
          <Chip
            key={filter.value}
            active={status === filter.value}
            onClick={() => onStatusChange(filter.value)}
          >
            {filter.label}
          </Chip>
        ))}
      </div>

      {categories.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <Chip active={category === 'all'} onClick={() => onCategoryChange('all')}>
            All categories
          </Chip>
          {categories.map((entry) => (
            <Chip
              key={entry.value}
              active={category === entry.value}
              onClick={() => onCategoryChange(entry.value)}
            >
              {humanizeEnum(entry.value)} ({entry.count})
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

export default CatalogFilters;
